/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */
import { Editor, EditorContent, useCurrentEditor, useEditorState } from "@tiptap/react";
import { Box, Stack, SxProps, Theme, Typography } from "@mui/material";
import { TiptapEditorToolbar } from "./TiptapEditorToolbar";
import { tiptapEditorStyle } from "./tiptapEditorStyle";
import { ValidatorType } from "@/src/types/ValidatorType";
import { useForm } from "@/src/provider/Form/FormProvider";
import _get from "lodash/get";
import { useEffect } from "react";

type TiptapEditorProps = {
  name: string;
  validators?: Array<ValidatorType>;
  dependentValidationFields?: Array<string>;
  showCharacterCount?: boolean;
  maxCharacterLength?: number;
  outerContainerSx?: SxProps<Theme>;
  containerSx?: SxProps<Theme>;
  toolbarSx?: SxProps<Theme>;
  editorSx?: SxProps<Theme>;
  characterCountSx?: SxProps<Theme>;
  errorSx?: SxProps<Theme>;
};

export const TiptapEditor = ({
  validators,
  dependentValidationFields,
  showCharacterCount = true,
  name,
  outerContainerSx,
  containerSx,
  toolbarSx,
  editorSx,
  characterCountSx,
  errorSx,
  maxCharacterLength,
}: TiptapEditorProps) => {
  const { editor } = useCurrentEditor();
  const { errors, setError, registerValidators, updateValue } = useForm();
  const error = !!_get(errors, name);

  useEffect(() => {
    const updateHandler = ({ editor }: { editor: Editor }) => {
      updateValue(name, editor.getHTML());
    };

    editor?.on("update", updateHandler);

    return () => {
      editor?.off("update", updateHandler);
    };
  }, []);

  useEffect(() => {
    registerValidators(name, validators, dependentValidationFields);
    return () => {
      registerValidators(name, [], []);
      setError(name, undefined);
    };
  }, [name]);

  const characters = useEditorState({
    editor,
    selector: ({ editor }) => {
      return editor?.storage.characterCount.characters();
    },
  });

  return (
    <Stack sx={{ gap: "0.25rem", maxWidth: "100%", ...outerContainerSx }}>
      <Box
        sx={{
          ...tiptapEditorStyle.tiptapContainer,
          ...(error ? tiptapEditorStyle.tiptapContainerError : {}),
          ...containerSx,
        }}
      >
        <TiptapEditorToolbar sx={toolbarSx} />
        <Box
          sx={{
            ...tiptapEditorStyle.editor,
            ...editorSx,
          }}
          component={EditorContent}
          editor={editor}
        />
      </Box>
      {showCharacterCount && (
        <Typography sx={characterCountSx}>
          {characters} {maxCharacterLength ? "/ " + maxCharacterLength : null}
        </Typography>
      )}
      {error && (
        <Typography
          variant={"body2"}
          align={"center"}
          color={"error"}
          sx={{
            padding: "0.125rem 0 1rem",
            ...errorSx,
          }}
        >
          <strong>{errors[name]}</strong>
        </Typography>
      )}
    </Stack>
  );
};
